import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const articles = [
  {
    slug: 'how-to-create-gst-invoice-india',
    title: 'How to Create a GST Invoice in India (2025 Guide)',
    description: 'Step-by-step guide to creating GST-compliant tax invoices. Covers mandatory fields, CGST/SGST/IGST breakdown, and common mistakes to avoid.',
    category: 'India GST',
    categoryColor: 'green',
    readTime: '6 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'what-to-include-freelance-invoice',
    title: 'What to Include on a Freelance Invoice (Legal Requirements)',
    description: 'Every element a freelance invoice must contain — from legal requirements to professional extras that get you paid faster.',
    category: 'Freelancing',
    categoryColor: 'blue',
    readTime: '5 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'how-to-invoice-international-clients',
    title: 'How to Invoice International Clients (Step-by-Step)',
    description: 'Currency choice, VAT rules, payment methods, and exactly what to put on an invoice when billing clients overseas.',
    category: 'International',
    categoryColor: 'indigo',
    readTime: '6 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'invoice-vs-receipt',
    title: 'Invoice vs Receipt — What\'s the Difference?',
    description: 'Invoices and receipts are not the same thing. Here\'s when to use each, what they must contain, and why the difference matters for your records.',
    category: 'Basics',
    categoryColor: 'slate',
    readTime: '4 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'vat-invoice-requirements-uk',
    title: 'VAT Invoice Requirements in the UK (2025)',
    description: 'Full and simplified UK VAT invoice requirements, time limits, and what happens if your invoice is missing mandatory details.',
    category: 'UK VAT',
    categoryColor: 'purple',
    readTime: '6 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'how-to-calculate-gst-invoice',
    title: 'How to Calculate GST on an Invoice (With Examples)',
    description: 'CGST, SGST, IGST — exactly how to calculate each one, worked examples at every GST slab, and the inclusive vs exclusive GST formula.',
    category: 'India GST',
    categoryColor: 'green',
    readTime: '5 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'late-payment-follow-up-email',
    title: 'How to Write a Late Payment Follow-Up Email (+ Templates)',
    description: 'Four ready-to-send email templates for chasing unpaid invoices — from a polite first reminder to a firm final notice.',
    category: 'Getting Paid',
    categoryColor: 'amber',
    readTime: '5 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'gst-invoice-format-india',
    title: 'GST Invoice Format Requirements India (2025)',
    description: 'Mandatory fields, format rules, e-invoicing thresholds, and the difference between a tax invoice and a bill of supply under Indian GST law.',
    category: 'India GST',
    categoryColor: 'green',
    readTime: '6 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'invoice-payment-terms',
    title: 'Net 30 vs Net 15 vs Due on Receipt — Invoice Payment Terms Explained',
    description: 'What every payment term means, which one freelancers and small businesses should use, and how to write payment terms on your invoice.',
    category: 'Basics',
    categoryColor: 'slate',
    readTime: '5 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'how-to-number-invoices',
    title: 'How to Number Your Invoices Professionally',
    description: 'Sequential, date-based, and client-based numbering formats — plus the mistakes that create accounting headaches and how to avoid them.',
    category: 'Basics',
    categoryColor: 'slate',
    readTime: '4 min',
    date: 'May 20, 2025',
  },
  {
    slug: 'what-does-due-upon-receipt-mean',
    title: 'What Does "Due Upon Receipt" Mean on an Invoice?',
    description: '"Due upon receipt" means payment is expected immediately. Covers all synonyms, how it compares to Net 30, whether it is enforceable, and when to use it.',
    category: 'Basics',
    categoryColor: 'slate',
    readTime: '5 min',
    date: 'June 1, 2026',
  },
  {
    slug: 'net-15-payment-terms',
    title: 'Net 15 Payment Terms — What It Means and When to Use It',
    description: 'Net 15 means payment due within 15 days of the invoice date. Covers what it means, Net 15 vs Net 30, which industries use it, and how to write it on your invoice.',
    category: 'Basics',
    categoryColor: 'slate',
    readTime: '5 min',
    date: 'June 1, 2026',
  },
  {
    slug: 'simplified-vat-invoice',
    title: 'Simplified VAT Invoice UK — Rules, Threshold & What to Include',
    description: 'A simplified VAT invoice can be used for UK retail supplies up to £250. Covers required fields, when to use it vs a full VAT invoice, and HMRC rules.',
    category: 'UK VAT',
    categoryColor: 'purple',
    readTime: '5 min',
    date: 'June 1, 2026',
  },
  {
    slug: 'uk-invoice-requirements',
    title: 'UK Invoice Requirements 2025 — What Must Be on a UK Invoice?',
    description: 'What every UK invoice must include — for sole traders, limited companies, VAT and non-VAT registered businesses. Based on HMRC and Companies Act rules.',
    category: 'UK VAT',
    categoryColor: 'purple',
    readTime: '6 min',
    date: 'June 1, 2026',
  },
  {
    slug: 'australia-tax-invoice-guide',
    title: 'Australian Tax Invoice Requirements — What the ATO Requires in 2025',
    description: 'What every Australian tax invoice must include — ABN, GST amount, "Tax Invoice" label, and the $1,000 buyer-name rule. For GST-registered and non-GST businesses.',
    category: 'Australia',
    categoryColor: 'sky',
    readTime: '6 min',
    date: 'June 1, 2026',
  },
  {
    slug: 'self-employed-invoice-guide',
    title: 'How to Invoice as Self-Employed — What to Include and How to Get Paid',
    description: 'Complete guide: what fields to include on a self-employed invoice, how to number invoices, what payment terms to use, VAT rules, and 6 tips to get paid faster.',
    category: 'Freelancing',
    categoryColor: 'blue',
    readTime: '6 min',
    date: 'June 1, 2026',
  },
  {
    slug: 'how-to-invoice-as-a-coach-uk',
    title: 'How to Invoice as a Coach in the UK — VAT, HMRC & Getting Paid',
    description: 'What to include on a UK coaching invoice, when to charge 20% VAT, HMRC self-assessment for coaches, and the payment terms that get you paid on time.',
    category: 'UK Invoicing',
    categoryColor: 'violet',
    readTime: '7 min',
    date: 'June 7, 2026',
  },
  {
    slug: 'how-to-invoice-as-freelancer-nigeria',
    title: 'How to Invoice as a Freelancer in Nigeria — FIRS, USD & Getting Paid',
    description: 'FIRS TIN registration, USD billing, VAT on export services, and the best ways to receive international payments as a Nigerian freelancer.',
    category: 'International',
    categoryColor: 'indigo',
    readTime: '6 min',
    date: 'June 7, 2026',
  },
  {
    slug: 'how-to-invoice-clients-kenya',
    title: 'How to Invoice Clients in Kenya — KRA, M-Pesa & eTIMS Guide',
    description: 'KRA PIN registration, 16% VAT threshold, KRA eTIMS electronic invoicing, M-Pesa payment details, and billing international clients from Kenya.',
    category: 'International',
    categoryColor: 'indigo',
    readTime: '6 min',
    date: 'June 7, 2026',
  },
  {
    slug: 'how-to-invoice-as-freelancer-south-africa',
    title: 'How to Invoice as a Freelancer in South Africa — SARS, VAT & Getting Paid',
    description: 'SARS VAT rules, 15% VAT threshold, full vs abridged tax invoices, and the best ways to receive international payments as a South African freelancer.',
    category: 'International',
    categoryColor: 'indigo',
    readTime: '7 min',
    date: 'June 7, 2026',
  },
  {
    slug: 'how-to-invoice-as-personal-trainer-uk',
    title: 'How to Invoice as a Personal Trainer in the UK — HMRC, VAT & Payment Terms',
    description: 'What goes on a UK personal trainer invoice, when 20% VAT applies, HMRC self-assessment for PTs, and the best billing model for session and retainer clients.',
    category: 'UK Invoicing',
    categoryColor: 'violet',
    readTime: '6 min',
    date: 'June 7, 2026',
  },
  {
    slug: 'how-to-invoice-as-photographer-uk',
    title: 'How to Invoice as a Photographer in the UK — HMRC, Licensing & Getting Paid',
    description: 'Deposit invoicing, image licensing line items, day rate billing, VAT at 20%, and HMRC self-assessment rules for UK freelance photographers.',
    category: 'UK Invoicing',
    categoryColor: 'violet',
    readTime: '7 min',
    date: 'June 7, 2026',
  },
];

const colorMap = {
  green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
  slate: 'bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  sky: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
  violet: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
};

export default function BlogIndexPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Blog: Guides for Freelancers and Businesses | Invilic"
        description="Free guides on invoicing, GST, VAT, getting paid, and running a freelance business. Practical articles written for freelancers and small business owners."
        canonical="https://www.invilic.com/blog"
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-14">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            Invoicing Guides &amp; Resources
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Practical guides on GST, VAT, international invoicing, and getting paid — written for freelancers and small businesses.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {articles.map(({ slug, title, description, category, categoryColor, readTime, date }) => (
            <Link
              key={slug}
              to={`/blog/${slug}`}
              className="group block border border-gray-100 dark:border-gray-800 rounded-2xl p-6 hover:border-blue-200 dark:hover:border-blue-800/50 hover:shadow-md transition-all duration-200 bg-white dark:bg-gray-900/30"
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colorMap[categoryColor]}`}>
                  {category}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                  <Clock className="w-3 h-3" /> {readTime}
                </span>
              </div>
              <h2 className="font-bold text-gray-900 dark:text-white mb-2 leading-snug group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                {description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400 dark:text-gray-500">{date}</span>
                <span className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 font-medium">
                  Read <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
